import { useEffect, useRef } from "react";
import { useRouteContext } from "@tanstack/react-router";
import { Container } from "react-bootstrap";
import { useWatch, type UseFormReturn } from "react-hook-form";
import { FaCalendarCheck } from "react-icons/fa";

import FormFieldRow from "@/components/shared/forms/FormFieldRow";
import ReadOnlySelect from "@/components/shared/forms/ReadOnlySelect";
import Select from "@/components/shared/forms/Select";
import TextInput from "@/components/shared/forms/TextInput";
import { type ATTN, type ENRL } from "@/models.ts";
import { useCoreData } from "@/queries.ts";
import {
  formatAttendanceMonth,
  getSelectableAttendanceMonths,
  useATTNFormLogic,
} from "./useATTNFormLogic";

export default function ATTNAddEditForm({
  selectedATTN,
  form,
  attnRecords,
  enrlRecords,
}: {
  selectedATTN: ATTN | undefined;
  form: UseFormReturn<ATTN>;
  attnRecords: ATTN[];
  enrlRecords: ENRL[];
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const coreData = useCoreData();
  const tooltipMap = leaContext.coreData.tooltipMap.get("ATTN");

  const viewOnly = !leaContext.hasRole("ATTNEdit");

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = form;

  const watchProgramYear = useWatch({ control, name: "ProgramYear" });
  const watchPreschoolCode = useWatch({ control, name: "PreschoolCode" });
  const watchFirstOfMonth = useWatch({ control, name: "FirstOfMonth" });

  const effectiveMonth = selectedATTN?.FirstOfMonth ?? watchFirstOfMonth;
  const { validators } = useATTNFormLogic(effectiveMonth);

  const monthOptions = selectedATTN
    ? []
    : getSelectableAttendanceMonths(
        watchProgramYear,
        watchPreschoolCode,
        enrlRecords,
        attnRecords
      );

  const preschoolOptions = leaContext
    .currentOrgPreschoolsByProgramYear(watchProgramYear)
    .map((x) => ({
      Text: x.PreschoolName,
      Value: x.PreschoolCDSCode,
    }));

  const isFirstProgramYearRender = useRef(true);
  useEffect(() => {
    if (selectedATTN) return;
    if (isFirstProgramYearRender.current) {
      isFirstProgramYearRender.current = false;
      return;
    }
    setValue("PreschoolCode", "", { shouldDirty: true, shouldValidate: false });
  }, [watchProgramYear, selectedATTN, setValue]);

  useEffect(() => {
    if (selectedATTN) return;
    setValue("FirstOfMonth", "", { shouldDirty: true, shouldValidate: false });
  }, [watchProgramYear, watchPreschoolCode, selectedATTN, setValue]);

  return (
    <Container
      className="ps-3 pe-3 overview-container"
      style={{ borderTop: "none", borderRadius: 0 }}
    >
      <div className="form-card-modern mb-3">
        <div className="section-header">
          <FaCalendarCheck className="section-icon" />
          <h5 className="section-title">Attendance Information</h5>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="CSPPID"
              labelText="CSPPID"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="ProgramYear"
              labelText="Program Year"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedATTN?.ProgramYear && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Program Year is required" }}
                    options={coreData.programYearList}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, {
                      required: "Program Year is required",
                    })}
                    options={coreData.programYearList}
                  />
                )
              }
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="PreschoolCode"
              labelText="Preschool"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                !!selectedATTN?.PreschoolCode && !viewOnly ? (
                  <ReadOnlySelect
                    id={id}
                    control={control}
                    name={name}
                    rules={{ required: "Preschool is required" }}
                    options={preschoolOptions}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, { required: "Preschool is required" })}
                    options={preschoolOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-6">
            <FormFieldRow
              name="LocalChildId"
              labelText="Local Child ID (CICN)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  disabled={viewOnly}
                  readOnly
                  {...register(name)}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-6">
            <FormFieldRow
              name="FirstOfMonth"
              labelText="Month"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) =>
                selectedATTN ? (
                  <TextInput
                    id={id}
                    disabled={viewOnly}
                    readOnly
                    value={formatAttendanceMonth(selectedATTN.FirstOfMonth)}
                    onChange={() => {}}
                  />
                ) : (
                  <Select
                    id={id}
                    disabled={viewOnly}
                    {...register(name, { required: "Month is required" })}
                    options={monthOptions}
                  />
                )
              }
            </FormFieldRow>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4">
            <FormFieldRow
              name="DaysExpected"
              labelText="Days Expected"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  type="number"
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Days Expected is required",
                    validate: validators.validateDaysExpected,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="DaysAttended"
              labelText="Days Attended"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
              isRequired
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  type="number"
                  disabled={viewOnly}
                  {...register(name, {
                    required: "Days Attended is required",
                    validate: validators.validateNonNegativeInteger,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="DaysAbsentExcused"
              labelText="Days Absent (Excused)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  type="number"
                  disabled={viewOnly}
                  {...register(name, {
                    validate: validators.validateOptionalNonNegativeInteger,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="DaysAbsentUnexcused"
              labelText="Days Absent (Unexcused)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  type="number"
                  disabled={viewOnly}
                  {...register(name, {
                    validate: validators.validateOptionalNonNegativeInteger,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="DaysAbsentSuspension"
              labelText="Days Absent (Suspension)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  type="number"
                  disabled={viewOnly}
                  {...register(name, {
                    validate: validators.validateOptionalNonNegativeInteger,
                  })}
                />
              )}
            </FormFieldRow>
          </div>

          <div className="col-md-4">
            <FormFieldRow
              name="DaysAttendedSuspension"
              labelText="Days Attended (Suspension)"
              errors={errors}
              tooltipMap={tooltipMap}
              labelCol={12}
              inputCol={12}
            >
              {(id, name) => (
                <TextInput
                  id={id}
                  type="number"
                  disabled={viewOnly}
                  {...register(name, {
                    validate: validators.validateOptionalNonNegativeInteger,
                  })}
                />
              )}
            </FormFieldRow>
          </div>
        </div>
      </div>
    </Container>
  );
}
